import compact from 'lodash/compact';
import flatMap from 'lodash/flatMap';
import includes from 'lodash/includes';
import uniq from 'lodash/uniq';

import {
  IAutoQuote,
  IVehicle,
  IAutoThirdPartyDataOrders,
  IDriver,
} from '../../../interfaces';
import getLatestDataOrders from '../getLatestDataOrders';

function getOffendingIdsForErrorCode(
  order: IAutoThirdPartyDataOrders,
  errorCode: string
) {
  return compact(
    (order?.error_details || []).map(
      (errorVehicle = {}) =>
        includes(errorVehicle.errors, errorCode) && errorVehicle.id
    )
  );
}

function getOffendingDriverLicenseInfoForErrorCode(
  order: IAutoThirdPartyDataOrders,
  errorCode: string
): {
  id?: string;
  driving_license_number?: string;
  driving_license_state?: string;
}[] {
  return compact(
    (order?.error_details || []).map(
      (errorObj = {}) =>
        includes(errorObj.errors, errorCode) && {
          id: errorObj.id,
          driving_license_number: errorObj.driving_license_number,
          driving_license_state: errorObj.driving_license_state,
        }
    )
  );
}

export function handleVinServicesError(
  quote: IAutoQuote,
  order: IAutoThirdPartyDataOrders,
  setIneligibleVehicleIds: (ids: string[]) => void
) {
  const unmatchedVinIds = getOffendingIdsForErrorCode(order, 'not_matched');
  const ineligibleVehicleIds = getOffendingIdsForErrorCode(order, 'ineligible');

  const handleNotMatched = (vehicle: IVehicle) => {
    return includes(unmatchedVinIds, vehicle.id)
      ? { ...vehicle, vin: null }
      : vehicle;
  };

  const handleIneligible = (vehicle: IVehicle) => {
    if (includes(ineligibleVehicleIds, vehicle.id)) {
      setIneligibleVehicleIds(uniq([...ineligibleVehicleIds, vehicle.id]));
    }
    return vehicle;
  };

  const updatedVehicles = (quote?.application?.vehicles || []).map((vehicle) =>
    [handleNotMatched, handleIneligible].reduce(
      (vehicle, handler) => handler(vehicle),
      vehicle
    )
  );

  const updatedQuote = {
    ...quote,
    application: {
      ...quote.application,
      vehicles: updatedVehicles,
    },
  };

  return updatedQuote;
}

export function handleClueError(
  quote: IAutoQuote,
  order: IAutoThirdPartyDataOrders,
  ineligibleDriverIds: (string | number)[],
  setIneligibleDriverIds: (ids: (string | number)[]) => void
) {
  const unmatchedDriverIds = getOffendingIdsForErrorCode(order, 'not_matched');
  const newIneligibleDriverIds = getOffendingIdsForErrorCode(
    order,
    'ineligible'
  );
  const invalidDriversLicenses = getOffendingDriverLicenseInfoForErrorCode(
    order,
    'invalid'
  );
  const handleNotMatched = (driver: IDriver) => {
    if (includes(unmatchedDriverIds, driver.id)) {
      setIneligibleDriverIds(uniq([...ineligibleDriverIds, driver.id]));
    }
    return driver;
  };

  const handleInvalid = (driver: IDriver) => {
    return invalidDriversLicenses.find((invalidDriver) => {
      return (
        invalidDriver.id === driver.id ||
        (invalidDriver.driving_license_number ===
          driver.driving_license_number &&
          invalidDriver.driving_license_state === driver.driving_license_state)
      );
    })
      ? {
          ...driver,
          driving_license_state: null,
          driving_license_number: null,
        }
      : driver;
  };

  const handleIneligible = (driver: IDriver) => {
    if (includes(newIneligibleDriverIds, driver.id)) {
      setIneligibleDriverIds(uniq([...ineligibleDriverIds, driver.id]));
    }
    return driver;
  };

  const updatedDrivers = (quote?.application?.drivers || []).map((driver) =>
    [handleNotMatched, handleIneligible, handleInvalid].reduce(
      (driver, handler) => handler(driver),
      driver
    )
  );

  const updatedQuote = {
    ...quote,
    application: {
      ...quote.application,
      drivers: updatedDrivers,
    },
  };

  return updatedQuote;
}

export function handleMvrError(
  quote: IAutoQuote,
  order: IAutoThirdPartyDataOrders,
  ineligibleDriverIds: (string | number)[],
  setIneligibleDriverIds: (ids: (string | number)[]) => void
) {
  const unmatchedDriverIds = getOffendingIdsForErrorCode(order, 'not_matched');
  const newIneligibleDriverIds = getOffendingIdsForErrorCode(
    order,
    'ineligible'
  );
  const invalidDriversLicenses = getOffendingDriverLicenseInfoForErrorCode(
    order,
    'invalid'
  );

  let mvrErrorDrivers = [...ineligibleDriverIds];

  const handleNotMatched = (driver: IDriver) => {
    if (includes(unmatchedDriverIds, driver.id)) {
      mvrErrorDrivers = uniq([...mvrErrorDrivers, driver.id]);
    }
    return driver;
  };

  const handleIneligible = (driver: IDriver) => {
    if (includes(newIneligibleDriverIds, driver.id)) {
      mvrErrorDrivers = uniq([...mvrErrorDrivers, driver.id]);
    }
    return driver;
  };

  const handleInvalid = (driver: IDriver) => {
    return invalidDriversLicenses.find((invalidDriver) => {
      return (
        invalidDriver.id === driver.id ||
        (invalidDriver.driving_license_number ===
          driver.driving_license_number &&
          invalidDriver.driving_license_state === driver.driving_license_state)
      );
    })
      ? {
          ...driver,
          driving_license_state: null,
          driving_license_number: null,
        }
      : driver;
  };

  const updatedDrivers = (quote?.application?.drivers || []).map((driver) =>
    [handleNotMatched, handleIneligible, handleInvalid].reduce(
      (driver, handler) => handler(driver),
      driver
    )
  );

  setIneligibleDriverIds(uniq(mvrErrorDrivers));

  const updatedQuote = {
    ...quote,
    application: {
      ...quote.application,
      drivers: updatedDrivers,
    },
  };

  return updatedQuote;
}

function getNewOrdersWithError(quote: IAutoQuote) {
  const orders = quote?.application?.metadata?.third_party_data_orders || [];
  const latestOrders = getLatestDataOrders(orders);
  const ordersWithError = latestOrders.filter((order) =>
    includes(['error', 'failure'], order?.status)
  );
  return ordersWithError;
}

export default function handleActionableDataOrderErrors(
  quote: IAutoQuote,
  ineligibleDriverIds: (string | number)[],
  setIneligibleDriverIds: (ids: (string | number)[]) => void,
  setIneligibleVehicleIds: (ids: string[]) => void,
  analytics?: any
) {
  const ordersWithError = getNewOrdersWithError(quote);

  if (!ordersWithError.length) {
    return quote;
  }
  const error_codes = uniq(
    flatMap(ordersWithError.map((order) => order?.error_codes))
  );
  analytics?.reportNotFound?.({ error_codes });

  return ordersWithError.reduce((updatedQuote, order) => {
    switch (order?.data_type) {
      case 'vin_services':
        return handleVinServicesError(
          updatedQuote,
          order,
          setIneligibleVehicleIds
        );
      case 'clue':
        return handleClueError(
          updatedQuote,
          order,
          ineligibleDriverIds,
          setIneligibleDriverIds
        );
      case 'mvr':
        return handleMvrError(
          updatedQuote,
          order,
          ineligibleDriverIds,
          setIneligibleDriverIds
        );
      default:
        return updatedQuote;
    }
  }, quote);
}
